import React, { useState, useEffect, useRef, InputHTMLAttributes, useMemo } from "react";
import Pagination from "./Pagination";
import NoResultsFound from "./NoResultsFound";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import CommonSearch from "./ui/CommonSearch";
import Tooltip from "./Tooltip";

// Helper for indeterminate checkbox
interface IndeterminateCheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  indeterminate?: boolean;
}

const IndeterminateCheckbox: React.FC<IndeterminateCheckboxProps> = ({
  indeterminate,
  ...rest
}) => {
  const ref = useRef<HTMLInputElement>(null!);

  useEffect(() => {
    if (typeof indeterminate === "boolean") {
      ref.current.indeterminate = indeterminate;
    }
  }, [ref, indeterminate]);

  return <input type="checkbox" ref={ref} {...rest} />;
};

// Types
export enum SortOrder {
  Asc = "asc",
  Desc = "desc",
}

export enum SortDirection {
  Ascending = "ascending",
  Descending = "descending",
}

interface BaseColumn {
  header?: React.ReactNode;
  headerName: React.ReactNode;
  /**
   * Optional longer tooltip shown on the column header. Falls back to
   * `headerName` (stringified) when omitted. Use this when the visible
   * header is an abbreviation ("ID") but hover should reveal the full
   * label ("Feedback record") to match legacy RoR behaviour.
   */
  headerTooltip?: string;
  sortable?: boolean;
  headerClassName?: string;
  cellClassName?: string;
  sticky?: boolean; // New property for sticky columns
  stickyLeft?: string; // Left position for sticky column
  stickyRight?: string; // Right position for sticky column
  showShadow?: boolean;
  showRightShadow?: boolean; // Show shadow after this column (right side)
  showLeftShadow?: boolean; // Show shadow before this column
  minWidth?: number;
  maxWidth?: number;
}

interface ConcreteColumn<T> extends BaseColumn {
  field: keyof T | string; // Allow string for custom accessors
  sortField?: string; // Optional: database field name to use for server-side sorting
  cell?: (row: T) => React.ReactNode;
  columns?: never; // Cannot have sub-columns
  /** Optional sub-header label rendered in a 3rd header row (RoR eco-float-tr-3 pattern) */
  subHeaderName?: React.ReactNode;
  /** Sort field for the sub-header (if sortable independently) */
  subSortField?: string;
}

interface GroupedColumn<T> extends BaseColumn {
  columns: ConcreteColumn<T>[];
  field?: never; // Cannot have a field
  cell?: never; // Cannot have a cell renderer
}

export type Column<T> = ConcreteColumn<T> | GroupedColumn<T>;

export interface SortConfig<T> {
  key: keyof T | string | null;
  direction: SortDirection;
}

type DataItem = {
  id: string | number;
};

// Sorting functions
function getSortValue<T>(item: T, key: keyof T | string) {
  if (!key) {
    return undefined;
  }

  return String(key)
    .split(".")
    .reduce<unknown>((value, segment) => {
      if (value === null || value === undefined || typeof value !== "object") {
        return undefined;
      }

      return (value as Record<string, unknown>)[segment];
    }, item);
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T | string) {
  const aValue = getSortValue(a, orderBy);
  const bValue = getSortValue(b, orderBy);

  if (typeof aValue === "string" && typeof bValue === "string") {
    return bValue.localeCompare(aValue);
  }

  if ((bValue as number) < (aValue as number)) {
    return -1;
  }
  if ((bValue as number) > (aValue as number)) {
    return 1;
  }
  return 0;
}

function getComparator<T>(
  order: SortOrder,
  orderBy: keyof T | string,
): (a: T, b: T) => number {
  return order === SortOrder.Desc
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number): T[] {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// Row Component
interface DataTableRowProps<T extends DataItem> {
  row: T;
  rowRenderKey: string;
  /** 1-based display row number accounting for current page and rows-per-page. Used in the checkbox aria-label so screen readers announce the correct position. */
  rowNumber: number;
  selected: (string | number)[];
  columns: Column<T>[];
  prefixColumns?: Column<T>[];
  renderSubComponent?: ((row: T) => React.ReactNode) | undefined;
  /** Renders an always-visible sub-row (no chevron toggle) spanning all columns. */
  renderInlineSubComponent?: ((row: T) => React.ReactNode) | undefined;
  onSelect: (id: string | number) => void;
  selectable: boolean | undefined;
  hideCheckboxes?: boolean;
  columnWidths?: { [key: string]: string } | undefined;
  onRowClick?: ((row: T) => void) | undefined;
  isRowSelectable?: ((row: T) => boolean) | undefined;
  rowClassName?: string;
  renderUnselectableCell?: ((row: T) => React.ReactNode) | undefined;
  rowHeight?: number | undefined;
  /** When true, the sub-component is expanded by default. */
  defaultOpen?: boolean;
  /** Called whenever this row's expand state changes. */
  onRowExpandChange?: (id: string | number, isOpen: boolean) => void;
}
const DataTableRowInner = <T extends DataItem>({
  row,
  rowRenderKey,
  rowNumber,
  selected,
  columns,
  prefixColumns,
  renderSubComponent,
  renderInlineSubComponent,
  onSelect,
  selectable,
  hideCheckboxes,
  columnWidths,
  onRowClick,
  isRowSelectable,
  rowClassName,
  renderUnselectableCell,
  truncateCells,
  rowHeight,
  defaultOpen,
  onRowExpandChange,
}: DataTableRowProps<T> & { truncateCells?: boolean }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen ?? false);
  const onRowExpandChangeRef = useRef(onRowExpandChange);
  onRowExpandChangeRef.current = onRowExpandChange;

  useEffect(() => {
    const next = defaultOpen ?? false;
    setOpen(next);
    onRowExpandChangeRef.current?.(row.id, next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOpen]);
  const [isHovered, setIsHovered] = useState(false);
  const isItemSelected = selected.includes(row.id.toString());
  const isRowCurrentlySelectable = isRowSelectable ? isRowSelectable(row) : true;

  const flatColumns = columns.flatMap((c) =>
    c.columns ? c.columns : [c],
  ) as ConcreteColumn<T>[];

  const subContent = renderSubComponent ? renderSubComponent(row) : null;
  const hasSubContent = subContent !== null && subContent !== undefined;

  const flatPrefixColumns = (prefixColumns || []).flatMap((c) =>
    c.columns ? c.columns : [c],
  ) as ConcreteColumn<T>[];

  // Calculate cell padding classes based on rowHeight
  const cellPaddingClass = rowHeight ? '' : 'py-2';
  const cellStyle = rowHeight ? { height: `${rowHeight}px` } : {};

  return (
    <>
      <tr
        role="row"
        aria-selected={isItemSelected}
        tabIndex={onRowClick ? 0 : -1}
        data-testid={`data-table-row-${row.id}`}
        className={`group/row ${isItemSelected ? (hideCheckboxes ? "" : "bg-primary/10") : "hover:bg-surface-alt"} ${onRowClick ? "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset" : ""} ${rowClassName ?? ""}`}
        style={isItemSelected && hideCheckboxes ? { backgroundColor: 'color-mix(in srgb, rgb(var(--color-primary)) 10%, rgb(var(--color-surface)))' } : undefined}
        onClick={() => onRowClick?.(row)}
        onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row); } } : undefined}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {flatPrefixColumns.map((column) => (
          <td
            key={`${row.id}-prefix-${String(column.field)}`}
            data-testid={`data-table-row-${row.id}-prefix-cell-${String(column.field)}`}
            style={{
              minWidth: column.minWidth ? `${column.minWidth}px` : columnWidths?.[String(column.field)],
              maxWidth: column.maxWidth ? `${column.maxWidth}px` : undefined,
              width: column.sticky ? undefined : '20px',
              ...cellStyle,
              ...(column.sticky
                ? {
                  position: 'sticky',
                  left: column.stickyLeft ?? '0px',
                  right: column.stickyRight,
                  zIndex: 2,
                  backgroundColor: isItemSelected
                    ? 'color-mix(in srgb, rgb(var(--color-primary)) 10%, rgb(var(--color-surface)))'
                    : isHovered
                      ? 'rgb(var(--color-surface-alt))'
                      : 'rgb(var(--color-surface))',
                }
                : {}),
            }}
            className={`px-0.5 ${cellPaddingClass} text-sm text-text text-left border-b border-sidebarHeaderBorder ${column.cellClassName ?? ''}`}
          >
            {column.cell ? column.cell(row) : (row[column.field as keyof T] as React.ReactNode)}
          </td>
        ))}
        {selectable && (
          <td
            className={`w-12 px-4 ${cellPaddingClass} sticky text-center border-b border-sidebarHeaderBorder`}
            onClick={(e) => e.stopPropagation()}
            style={{
              minWidth: '48px',
              width: '48px',
              position: 'sticky',
              zIndex: 2,
              left: (() => {
                const stickyPrefixCols = flatPrefixColumns.filter(c => c.sticky);
                if (stickyPrefixCols.length === 0) return '0px';
                const totalWidth = stickyPrefixCols.reduce((acc, c) => {
                  const colWidth = parseInt(columnWidths?.[String(c.field)] ?? '40') || 40;
                  return acc + colWidth;
                }, 0);
                return `${totalWidth}px`;
              })(),
              backgroundColor: isItemSelected
                ? 'color-mix(in srgb, rgb(var(--color-primary)) 10%, rgb(var(--color-surface)))'
                : isHovered
                  ? 'rgb(var(--color-surface-alt))'
                  : 'rgb(var(--color-surface))',
              ...cellStyle,
            }}
          >
            {!hideCheckboxes && (!isRowCurrentlySelectable && renderUnselectableCell ? (
              renderUnselectableCell(row)
            ) : (
              <input
                type="checkbox"
                data-testid={`data-table-row-${row.id}-checkbox`}
                className="h-4 w-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                checked={isItemSelected}
                onChange={() => onSelect(row.id.toString())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onSelect(row.id.toString());
                  }
                }}
                aria-label={`Select row ${rowNumber}, ${isItemSelected ? t('common.checked', 'checked') : t('common.notChecked', 'not checked')}`}
                disabled={!isRowCurrentlySelectable}
              />
            ))}
          </td>
        )}
        {renderSubComponent && hasSubContent && (
          <td className={`w-12 px-3 ${cellPaddingClass} text-left border-b border-sidebarHeaderBorder`} style={cellStyle}>
            <button
              data-testid={`data-table-row-${row.id}-expand-button`}
              aria-label={open ? "Collapse row" : "Expand row"}
              aria-expanded={open}
              onClick={(e) => {
                e.stopPropagation();
                const next = !open;
                setOpen(next);
                onRowExpandChangeRef.current?.(row.id, next);
              }}
              className="text-text-muted hover:text-text"
            >
              {open ? <ChevronUp size={20} aria-hidden="true" /> : <ChevronDown size={20} aria-hidden="true" />}
            </button>
          </td>
        )}
        {renderSubComponent && !hasSubContent && <td className="w-0 p-0"></td>}
        {flatColumns.map((column, columnIndex) => (
          <td
            key={`${row.id}-${String(column.field)}-${columnIndex}`}
            data-testid={`data-table-row-${row.id}-cell-${String(column.field)}-${columnIndex}`}
            style={{
              minWidth: column.minWidth ? `${column.minWidth}px` : columnWidths?.[String(column.field)],
              maxWidth: column.maxWidth ? `${column.maxWidth}px` : undefined,
              width: column.minWidth ? `${column.minWidth}px` : undefined,
              whiteSpace: (column.minWidth || column.maxWidth) ? 'normal' : undefined,
              wordBreak: (column.minWidth || column.maxWidth) ? 'break-word' : undefined,
              ...cellStyle,
              ...(column.sticky
                ? {
                  position: 'sticky',
                  left: column.stickyLeft,
                  right: column.stickyRight,
                  zIndex: 2,
                  backgroundColor: isItemSelected
                    ? 'color-mix(in srgb, rgb(var(--color-primary)) 10%, rgb(var(--color-surface)))'
                    : isHovered
                      ? 'rgb(var(--color-surface-alt))'
                      : 'rgb(var(--color-surface))',
                }
                : {}),
            }}
            className={`px-4 ${cellPaddingClass} text-sm text-text text-left relative border-b border-sidebarHeaderBorder ${truncateCells ? 'overflow-hidden text-ellipsis whitespace-nowrap' : ''} ${column.cellClassName ?? ''} ${column.showRightShadow ? 'after:absolute after:top-0 after:right-0 after:bottom-0 after:w-[30px] after:translate-x-full after:pointer-events-none after:shadow-[inset_10px_0_8px_-8px_rgba(0,0,0,0.15)]' : ''} ${column.showLeftShadow ? 'before:absolute before:top-0 before:left-0 before:bottom-0 before:w-[30px] before:-translate-x-full before:pointer-events-none before:shadow-[inset_-10px_0_8px_-8px_rgba(0,0,0,0.15)]' : ''}`}
          >
            {column.cell ? column.cell(row) : (row[column.field as keyof T] as React.ReactNode)}
          </td>
        ))}
      </tr>
      {renderSubComponent && open && hasSubContent && (
        <tr data-testid={`data-table-row-${row.id}-expanded-${rowRenderKey}`}>
          <td
            colSpan={flatColumns.length + (selectable ? 1 : 0) + 1}
            className="p-0"
          >
            <div
              className="p-4 bg-surface-alt"
              data-testid={`data-table-row-${row.id}-expanded-content`}
            >
              {subContent}
            </div>
          </td>
        </tr>
      )}
      {renderInlineSubComponent && (() => {
        const inlineContent = renderInlineSubComponent(row);
        if (inlineContent === null || inlineContent === undefined) return null;
        return (
          <tr data-testid={`data-table-row-${row.id}-inline-sub-${rowRenderKey}`}>
            <td
              colSpan={flatColumns.length + (selectable ? 1 : 0) + (renderSubComponent ? 1 : 0) + flatPrefixColumns.length}
              className="border-b border-sidebarHeaderBorder p-0"
            >
              {inlineContent}
            </td>
          </tr>
        );
      })()}
    </>
  );
};

// Memoize row component — prevents re-rendering all rows when parent state
// changes (e.g., pagination, sorting, selection of a different row).
const DataTableRow = React.memo(DataTableRowInner) as typeof DataTableRowInner;

// Main Table Component

interface DataTableProps<T extends DataItem> {
  "data-testid": string;
  data: T[];
  columns: Column<T>[];
  prefixColumns?: Column<T>[]; // Columns rendered before the checkbox
  onSelectionChange?: (selected: (string | number)[]) => void;
  renderSubComponent?: (row: T) => React.ReactNode;
  /** Renders an always-visible sub-row (no chevron toggle) spanning all columns. */
  renderInlineSubComponent?: (row: T) => React.ReactNode;
  /**
   * When provided, renders a full-width <tr> at the very top of <tbody>, before any data rows.
   * Useful for "N items hidden above" banners that must span all columns.
   */
  renderTopBanner?: React.ReactNode;
  selectable?: boolean;
  hideCheckboxes?: boolean; // Hide checkboxes while keeping selection state
  totalCount: number;
  page: number;
  onPageChange: (page: number) => void;
  rowsPerPage: number;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  rowsPerPageOptions?: number[];
  columnWidths?: { [key: string]: string };
  sortConfig?: SortConfig<T>;
  requestSort?: (key: keyof T) => void;
  onSelectAll?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  headerCheckboxRef?: React.RefObject<HTMLInputElement>;
  isLoading: boolean;
  error: unknown;
  onRowClick?: (row: T) => void;
  hidePagination?: boolean;
  stickyPagination?: boolean;
  serverSideSorting?: boolean; // When true, delegates sorting/pagination to the server — skips client-side slicing
  externalSelected?: (string | number)[]; // External selection control
  isRowSelectable?: (row: T) => boolean;
  renderUnselectableCell?: (row: T) => React.ReactNode;
  showSearch?: boolean;
  searchTerm?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  searchWrapperClassName?: string;
  searchContainerClassName?: string;
  searchAdornment?: React.ReactNode;
  fixedLayout?: boolean;
  truncateCells?: boolean;
  defaultSortKey?: keyof T | string;
  defaultSortOrder?: SortOrder;
  getRowClassName?: (row: T) => string;
  rowHeight?: number; // Custom row height in pixels (default: auto with py-2 padding)
  /**
   * When true, the DataTable root does NOT use h-full/min-h-0 flex stretch.
   * Use this when the DataTable is embedded inside a naturally-scrolling page
   * (e.g. a tab panel that should scroll the page body), instead of a
   * fixed-height flex container (e.g. a full-viewport layout with sidebar).
   */
  naturalHeight?: boolean;
  /**
   * When true (and combined with naturalHeight), the table header will remain
   * sticky at the top of the viewport as the user scrolls down the page.
   * Requires naturalHeight=true since fixed-height DataTables already have
   * their own internal scroll container where sticky works natively.
   */
  stickyHeader?: boolean;
  /**
   * Draw the sort icon the way legacy drt_ui does (icon.rb: ascending = fa-sort-down,
   * descending = fa-sort-up), so a newest-first table shows ↑ as dispatchers are used to.
   * Only the icon changes; `sortConfig` and `aria-sort` keep the real direction.
   */
  legacySortIcons?: boolean;
  /** When true, all expandable rows will be expanded by default. */
  expandAllRows?: boolean;
  /**
   * Optional content rendered inside the expand-toggle column header `<th>`.
   * Use this to place a global collapse/expand-all button aligned with the
   * per-row expand chevrons.
   */
  renderExpandColumnHeader?: React.ReactNode;
  /** Called whenever any row's expand state changes (user toggle or programmatic). */
  onRowExpandChange?: (id: string | number, isOpen: boolean) => void;
  /** Accessible label for the table. Used by screen readers to identify the table and context-qualify "Select all rows". */
  tableLabel?: string;
}

const DataTable = <T extends DataItem>({
  "data-testid": dataTestId,
  data = [],
  columns = [],
  prefixColumns,
  onSelectionChange,
  onRowClick,
  renderSubComponent,
  renderInlineSubComponent,
  selectable,
  hideCheckboxes = false,
  totalCount,
  page,
  onPageChange,
  rowsPerPage,
  onRowsPerPageChange,
  rowsPerPageOptions,
  columnWidths,
  hidePagination = false,
  isLoading,
  error,
  sortConfig,
  requestSort,
  serverSideSorting = false,
  externalSelected,
  isRowSelectable,
  renderUnselectableCell,
  showSearch = false,
  searchTerm = "",
  onSearchChange,
  searchPlaceholder = "Search...",
  searchWrapperClassName = "mb-4",
  searchContainerClassName = "",
  searchAdornment,
  fixedLayout = false,
  truncateCells = false,
  defaultSortKey = "",
  defaultSortOrder = SortOrder.Asc,
  getRowClassName,
  rowHeight,
  naturalHeight = false,
  stickyHeader = false,
  legacySortIcons = false,
  renderTopBanner,
  expandAllRows,
  renderExpandColumnHeader,
  onRowExpandChange,
  tableLabel,
}: DataTableProps<T>) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const resolvedDefaultSortKey = useMemo(() => {
    if (serverSideSorting || defaultSortKey) {
      return defaultSortKey;
    }

    const flatColumns = columns.flatMap((column) =>
      "columns" in column && column.columns ? column.columns : [column]
    );

    const firstSortableColumn = flatColumns.find((column) => {
      const hasSortableFlag = "sortable" in column && column.sortable === true;
      const hasSorter =
        "sorter" in (column as object) &&
        (column as { sorter?: boolean | ((a: T, b: T) => number) }).sorter !== undefined;

      return hasSortableFlag || hasSorter;
    });

    if (firstSortableColumn) {
      if ("field" in firstSortableColumn && firstSortableColumn.field) {
        return firstSortableColumn.field;
      }

      if ("dataIndex" in (firstSortableColumn as object)) {
        return (firstSortableColumn as { dataIndex?: keyof T | string }).dataIndex || "";
      }
    }

    const firstColumnWithKey = flatColumns.find((column) => {
      if ("field" in column && column.field) {
        return true;
      }

      return "dataIndex" in (column as object) && Boolean((column as { dataIndex?: keyof T | string }).dataIndex);
    });

    if (firstColumnWithKey) {
      if ("field" in firstColumnWithKey && firstColumnWithKey.field) {
        return firstColumnWithKey.field;
      }

      if ("dataIndex" in (firstColumnWithKey as object)) {
        return (firstColumnWithKey as { dataIndex?: keyof T | string }).dataIndex || "";
      }
    }

    return "";
  }, [columns, defaultSortKey, serverSideSorting]);

  const [order, setOrder] = useState<SortOrder>(defaultSortOrder);
  const [orderBy, setOrderBy] = useState<keyof T | string>(resolvedDefaultSortKey);
  const onSelectionChangeRef = useRef(onSelectionChange);

  const areSelectionsEqual = (
    left: (string | number)[],
    right: (string | number)[]
  ): boolean => {
    if (left.length !== right.length) {
      return false;
    }

    return left.every((value, index) => value === right[index]);
  };

  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  useEffect(() => {
    if (!serverSideSorting && !orderBy && resolvedDefaultSortKey) {
      setOrder(SortOrder.Asc);
      setOrderBy(resolvedDefaultSortKey);
    }
  }, [orderBy, resolvedDefaultSortKey, serverSideSorting]);

  // Sync internal selection with external selection when provided
  useEffect(() => {
    if (externalSelected !== undefined) {
      setSelected((prevSelected) =>
        areSelectionsEqual(prevSelected, externalSelected) ? prevSelected : externalSelected
      );
    }
  }, [externalSelected]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Header "select all" is scoped to the current page only — in client-side
    // paginated tables `data` holds every row across every page, so we slice
    // down to `currentRows`. In server-side paginated tables `currentRows`
    // equals `data` (the backend already returned a single page), so this is
    // a no-op for them.
    const pageSelectableIds = currentRows
      .filter((row) => (isRowSelectable ? isRowSelectable(row) : true))
      .map((n) => n.id.toString());

    if (event.target.checked) {
      // Merge the current page's IDs into the existing selection so that
      // selections made on other pages are preserved.
      const merged = Array.from(new Set([...selected.map(String), ...pageSelectableIds]));
      setSelected(merged);
      onSelectionChangeRef.current?.(merged);
      return;
    }

    // Unchecking removes only the current page's IDs from the selection.
    const pageIdSet = new Set(pageSelectableIds);
    const remaining = selected.filter((id) => !pageIdSet.has(id.toString()));
    setSelected(remaining);
    onSelectionChangeRef.current?.(remaining);
  };

  const handleSelect = (id: string | number) => {
    const idAsString = id.toString();
    const selectedIndex = selected.indexOf(idAsString);
    let newSelected: (string | number)[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, idAsString);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
    onSelectionChangeRef.current?.(newSelected);
  };

  const handleRequestSort = (property: keyof T | string) => {
    if (serverSideSorting && requestSort) {
      // Server-side sorting: delegate to parent component
      requestSort(property as keyof T);
    } else {
      // Client-side sorting: update local state
      const isAsc = orderBy === property && order === SortOrder.Asc;
      setOrder(isAsc ? SortOrder.Desc : SortOrder.Asc);
      setOrderBy(property);
    }
    // Clear selection and reset to first page when sorting changes
    setSelected([]);
    onPageChange(0);
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  // Apply client-side sorting only when server-side sorting is disabled
  const processedRows = data && data.length > 0 && !serverSideSorting && orderBy
    ? stableSort([...data], getComparator(order, orderBy))
    : data;

  const currentRows = serverSideSorting
    ? processedRows
    : processedRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const handlePageChange = (pageNumber: number) => {
    onPageChange(pageNumber - 1);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    onRowsPerPageChange(event);
    onPageChange(0);
  };

  // Scoped to the current page so the header checkbox reflects only the
  // rows currently visible. For server-side paginated tables `currentRows`
  // and `data` are identical, so this is behaviour-equivalent.
  const selectablePageRows = isRowSelectable ? currentRows.filter(isRowSelectable) : currentRows;
  const selectablePageRowCount = selectablePageRows.length;
  const allPageRowsSelected = selectablePageRowCount > 0 && selectablePageRows.every(row => selected.includes(row.id.toString()));
  const somePageRowsSelected = selectablePageRowCount > 0 && selectablePageRows.some(row => selected.includes(row.id.toString())) && !allPageRowsSelected;
  const hasGroupedHeaders = columns.some((c) => c.columns);

  const renderSortIcon = (field: keyof T | string) => {
    // Use sortConfig for server-side sorting, otherwise use local state
    const currentSortKey = serverSideSorting && sortConfig ? sortConfig.key : orderBy;
    const currentSortDirection = serverSideSorting && sortConfig
      ? (sortConfig.direction === SortDirection.Ascending ? SortOrder.Asc : SortOrder.Desc)
      : order;

    if (currentSortKey === field) {
      return (currentSortDirection === SortOrder.Asc) !== legacySortIcons ? (
        <FaSortUp className="ml-1" aria-hidden="true" style={{ color: '#CAD5E2' }} />
      ) : (
        <FaSortDown className="ml-1" aria-hidden="true" style={{ color: '#CAD5E2' }} />
      );
    }
    return <FaSort className="ml-1" aria-hidden="true" style={{ color: '#CAD5E2' }} />;
  };

  /** Returns the aria-sort value for a column header */
  const getAriaSortValue = (field: keyof T | string): "ascending" | "descending" | "none" | undefined => {
    const currentSortKey = serverSideSorting && sortConfig ? sortConfig.key : orderBy;
    const currentSortDirection = serverSideSorting && sortConfig
      ? (sortConfig.direction === SortDirection.Ascending ? SortOrder.Asc : SortOrder.Desc)
      : order;
    if (currentSortKey === field) {
      return currentSortDirection === SortOrder.Asc ? "ascending" : "descending";
    }
    return "none";
  };

  const HeaderCell = ({
    col,
    truncateCells,
  }: {
    col: ConcreteColumn<T> | GroupedColumn<T>;
    truncateCells?: boolean;
  }) => {
    // Use sortField if available for server-side sorting, otherwise use field
    const sortKey = ('sortField' in col && col.sortField) ? col.sortField : col.field;
    // Prefer explicit headerTooltip, fall back to stringified headerName.
    const titleText = col.headerTooltip || (typeof col.headerName === 'string' ? col.headerName : '');

    return (
      <th
          scope="col"
          tabIndex={0}
          aria-sort={col.sortable && sortKey ? getAriaSortValue(sortKey) : undefined}
          style={{
            minWidth: col.minWidth ? `${col.minWidth}px` : (col.field ? columnWidths?.[String(col.field)] : undefined),
            maxWidth: col.maxWidth ? `${col.maxWidth}px` : undefined,
            width: col.minWidth ? `${col.minWidth}px` : undefined,
            whiteSpace: (col.minWidth || col.maxWidth) ? 'normal' : undefined,
            wordBreak: (col.minWidth || col.maxWidth) ? 'break-word' : undefined,
            ...(col.sticky
              ? {
                  position: 'sticky',
                  left: col.stickyLeft,
                  right: col.stickyRight,
                  zIndex: 20,
                  backgroundColor: 'rgb(var(--color-surface-alt))',
                }
              : {}),
          }}
          className={`px-3 h-10 text-left text-resetButton text-sm font-medium border-b border-sidebarHeaderBorder tableHeader relative ${!(col.minWidth || col.maxWidth) ? 'whitespace-nowrap' : ''} ${truncateCells ? 'overflow-hidden text-ellipsis' : ''} ${col.sortable ? 'cursor-pointer' : ''} ${col.headerClassName ?? ''} ${col.showRightShadow ? 'after:absolute after:top-0 after:right-0 after:bottom-0 after:w-[30px] after:translate-x-full after:pointer-events-none after:shadow-[inset_10px_0_8px_-8px_rgba(0,0,0,0.15)]' : ''} ${col.showLeftShadow ? 'before:absolute before:top-0 before:left-0 before:bottom-0 before:w-[30px] before:-translate-x-full before:pointer-events-none before:shadow-[inset_-10px_0_8px_-8px_rgba(0,0,0,0.15)]' : ''}`}
          onClick={() => col.sortable && sortKey && handleRequestSort(sortKey)}
          onKeyDown={(e) => {
            if (col.sortable && sortKey && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              handleRequestSort(sortKey);
            }
          }}>
        {titleText ? (
          <Tooltip text={titleText} position="top" usePortal={true}>
            <div className={`flex items-center ${col.headerClassName?.includes('text-center') ? 'justify-center' : ''}`}>
              {col.header || col.headerName}
              {col.sortable && sortKey && renderSortIcon(sortKey)}
            </div>
          </Tooltip>
        ) : (
          <div className={`flex items-center ${col.headerClassName?.includes('text-center') ? 'justify-center' : ''}`}>
            {col.header || col.headerName}
            {col.sortable && sortKey && renderSortIcon(sortKey)}
          </div>
        )}
      </th>
    );
  };

  return (
    <div className={`flex flex-col font-sans${naturalHeight ? '' : ' h-full min-h-0'}`} data-testid={dataTestId}>
      {showSearch && onSearchChange && (
        <div className={searchWrapperClassName}>
          <div className="flex flex-wrap items-center gap-3">
            <CommonSearch
              value={searchTerm}
              onChange={onSearchChange}
              placeholder={searchPlaceholder}
              containerClassName={searchContainerClassName}
            />
            {searchAdornment}
          </div>
        </div>
      )}
      <div className={`bg-surface rounded-[8px] shadow-[0_0_4px_0_rgba(0,0,0,0.16)] relative flex flex-col${naturalHeight ? '' : ' flex-1 min-h-0'}`}>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center z-20 bg-surface/60">
            <div className="flex items-center">
              <Loader2 className="mr-2 h-6 w-6 animate-spin" aria-hidden="true" />
              <span>{t('common.loading')}</span>
            </div>
          </div>
        )}
        {!naturalHeight && !isLoading && !error && currentRows.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <NoResultsFound />
          </div>
        )}
        {/* Visually-hidden live region announces loading/error state changes to screen readers (M10) */}
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {isLoading
            ? t('common.table.loading', 'Loading {{label}}...', { label: tableLabel ?? t('common.table.data', 'table data') })
            : error
            ? t('common.table.errorLoading', 'Error loading {{label}}', { label: tableLabel ?? t('common.table.data', 'table data') })
            : totalCount > 0
            ? t('common.table.itemsLoaded', '{{count}} {{label}} loaded', { count: totalCount, label: tableLabel ?? t('common.table.items', 'items') })
            : ''}
        </div>
        <div
          className={`${
            naturalHeight && stickyHeader
              ? ''
              : naturalHeight
              ? 'overflow-x-auto'
              : `h-full overflow-x-auto overflow-y-auto${isLoading || (!error && currentRows.length === 0) ? ' min-h-[400px]' : ''}`
          }`}
        >
          <table
            // No explicit role: this table has no roving-tabindex/arrow-key cell
            // navigation, so role="grid" (which it carried previously) tells
            // screen readers to switch into 2D grid-navigation mode without
            // actually supporting it — that stranded users after the last
            // focusable header cell instead of continuing into the row data.
            // The native <table>/<th scope="col">/<td> markup already gets the
            // correct implicit table/columnheader/cell roles without it.
            className="min-w-full border-separate border-spacing-0"
            style={fixedLayout ? { tableLayout: 'fixed', width: '100%' } : undefined}
            data-testid={`${dataTestId}-table`}
            aria-label={tableLabel}
            aria-busy={isLoading}
          >
          <thead
            className={`sticky z-20 shadow-sm${stickyHeader ? '' : ' top-0'}`}
            style={{
              backgroundColor: 'rgb(var(--color-surface-alt))',
              ...(stickyHeader ? { top: 'var(--sticky-header-offset, 0px)' } : {}),
            }}
            data-testid={`${dataTestId}-thead`}
          >
            {hasGroupedHeaders ? (
                <>
                    {/* Check if any flat column has a subHeaderName — if so, render 3 header rows */}
                    {(() => {
                      const flatCols = columns.flatMap(c => c.columns || [c]) as ConcreteColumn<T>[];
                      const hasSubHeaders = flatCols.some(c => c.subHeaderName);
                      const totalRowSpan = hasSubHeaders ? 3 : 2;
                      return null; // just for the variable computation
                    })()}
                    <tr
                      data-testid={`${dataTestId}-header-row-grouped`}
                      style={{ backgroundColor: 'rgb(var(--color-surface-alt))' }}
                    >
                        {(prefixColumns || []).flatMap(c => c.columns ? c.columns : [c]).map((col, index) => (
                          <th
                            key={`prefix-header-${index}`}
                            scope="col"
                            tabIndex={0}
                            rowSpan={(() => { const flatCols = columns.flatMap(c => c.columns || [c]) as ConcreteColumn<T>[]; return flatCols.some(c => c.subHeaderName) ? 3 : 2; })()}
                            className="px-0.5 py-2 text-left text-sm font-medium border-b border-sidebarHeaderBorder"
                            style={{
                              width: col.sticky ? undefined : '20px',
                              minWidth: col.field ? columnWidths?.[String(col.field)] : undefined,
                              backgroundColor: 'rgb(var(--color-surface-alt))',
                              ...(col.sticky ? { position: 'sticky', left: col.stickyLeft ?? '0px', zIndex: 32 } : {}),
                            }}
                          >
                            {col.headerName}
                          </th>
                        ))}
                        {selectable && (hideCheckboxes
                          ? <th rowSpan={(() => { const flatCols = columns.flatMap(c => c.columns || [c]) as ConcreteColumn<T>[]; return flatCols.some(c => c.subHeaderName) ? 3 : 2; })()} className="border-b border-sidebarHeaderBorder w-12 px-3 py-2 text-center sticky z-20" style={{ minWidth: '48px', width: '48px', backgroundColor: 'rgb(var(--color-surface-alt))', left: (() => { const stickyPfx = (prefixColumns || []).flatMap(c => c.columns ? c.columns : [c]).filter(c => c.sticky); if (stickyPfx.length === 0) return '0px'; return `${stickyPfx.reduce((acc, c) => acc + (parseInt(columnWidths?.[String(c.field)] ?? '40') || 40), 0)}px`; })() }} aria-hidden="true" />
                          : <th rowSpan={(() => { const flatCols = columns.flatMap(c => c.columns || [c]) as ConcreteColumn<T>[]; return flatCols.some(c => c.subHeaderName) ? 3 : 2; })()} className="border-b border-sidebarHeaderBorder w-12 px-3 py-2 text-center sticky z-20" style={{ backgroundColor: 'rgb(var(--color-surface-alt))', left: (() => { const stickyPfx = (prefixColumns || []).flatMap(c => c.columns ? c.columns : [c]).filter(c => c.sticky); if (stickyPfx.length === 0) return '0px'; return `${stickyPfx.reduce((acc, c) => acc + (parseInt(columnWidths?.[String(c.field)] ?? '40') || 40), 0)}px`; })() }}><IndeterminateCheckbox className="h-4 w-4 rounded border-border text-primary focus:ring-primary cursor-pointer" data-testid={`${dataTestId}-select-all-checkbox`} onChange={handleSelectAllClick} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); const syntheticEvent = { target: { checked: !allPageRowsSelected } } as React.ChangeEvent<HTMLInputElement>; handleSelectAllClick(syntheticEvent); } }} checked={allPageRowsSelected} indeterminate={somePageRowsSelected} aria-label={`${tableLabel ? `Select all rows in ${tableLabel}` : "Select all rows"}, ${somePageRowsSelected ? 'partially checked' : allPageRowsSelected ? 'checked' : 'not checked'}`}/></th>
                        )}
                        {renderSubComponent && (
                          <th
                            rowSpan={(() => { const flatCols = columns.flatMap(c => c.columns || [c]) as ConcreteColumn<T>[]; return flatCols.some(c => c.subHeaderName) ? 3 : 2; })()}
                            className="w-12 px-3 text-center"
                          >
                            {renderExpandColumnHeader ?? null}
                          </th>
                        )}
                        {columns.map((column, index) => {
                          const titleText = column.headerTooltip || (typeof column.headerName === 'string' ? column.headerName : '');
                          if (column.columns) {
                            return <th key={index} scope="col" tabIndex={0} colSpan={column.columns.length} style={column.sticky ? { position: 'sticky', left: column.stickyLeft, right: column.stickyRight, zIndex: 32, backgroundColor: 'rgb(var(--color-surface-alt))' } : { backgroundColor: 'rgb(var(--color-surface-alt))' }} className={`text-sm font-medium text-resetButton border-b border-sidebarHeaderBorder w-12 px-3 py-2 text-center relative after:absolute after:left-0 after:right-0 after:bottom-0 after:h-px ${column.headerClassName ?? ''}`}>{titleText ? <Tooltip text={titleText} position="top" usePortal={true}><span>{column.header || column.headerName}</span></Tooltip> : (column.header || column.headerName)}</th>
                          }
                            const sortKey = ('sortField' in column && column.sortField) ? column.sortField : column.field;
                            const _flatCols = columns.flatMap(c => c.columns || [c]) as ConcreteColumn<T>[];
                            const _hasSubH = _flatCols.some(c => c.subHeaderName);
                            // If this column has its own subHeaderName, only span 2 rows (leave room for row 3)
                            const _colHasSubH = 'subHeaderName' in column && (column as ConcreteColumn<T>).subHeaderName;
                            const _rowSpan = _hasSubH ? (_colHasSubH ? 2 : 3) : 2;
                            return <th
                                key={index}
                                scope="col"
                                tabIndex={0}
                                rowSpan={_rowSpan}
                                aria-sort={column.sortable && sortKey ? getAriaSortValue(sortKey) : undefined}
                                style={{
                                    minWidth: column.minWidth ? `${column.minWidth}px` : undefined,
                                    maxWidth: column.maxWidth ? `${column.maxWidth}px` : undefined,
                                    width: column.minWidth ? `${column.minWidth}px` : undefined,
                                    whiteSpace: (column.minWidth || column.maxWidth) ? 'normal' : undefined,
                                    wordBreak: (column.minWidth || column.maxWidth) ? 'break-word' : undefined,
                                    ...(column.sticky ? {
                                      position: 'sticky',
                                      left: column.stickyLeft,
                                      right: column.stickyRight,
                                      zIndex: 35,
                                      backgroundColor: 'rgb(var(--color-surface-alt))'
                                    } : {})
                                }}
                                className={`px-4 py-3 text-left text-sm font-medium text-resetButton border-b border-sidebarHeaderBorder relative ${!(column.minWidth || column.maxWidth) ? 'whitespace-nowrap' : ''} ${truncateCells ? 'overflow-hidden text-ellipsis whitespace-nowrap' : ''} ${column.sortable ? 'cursor-pointer' : ''} ${column.headerClassName ?? ''} ${column.showRightShadow ? 'after:absolute after:top-0 after:right-0 after:bottom-0 after:w-[30px] after:translate-x-full after:pointer-events-none after:shadow-[inset_10px_0_8px_-8px_rgba(0,0,0,0.15)]' : ''} ${column.showLeftShadow ? 'before:absolute before:top-0 before:left-0 before:bottom-0 before:w-[30px] before:-translate-x-full before:pointer-events-none before:shadow-[inset_-10px_0_8px_-8px_rgba(0,0,0,0.15)]' : ''}`}
                                onClick={() => column.sortable && sortKey && handleRequestSort(sortKey)}
                                onKeyDown={(e) => {
                                  if (column.sortable && sortKey && (e.key === 'Enter' || e.key === ' ')) {
                                    e.preventDefault();
                                    handleRequestSort(sortKey);
                                  }
                                }}
                            >
                                {titleText ? (
                                  <Tooltip text={titleText} position="top" usePortal={true}>
                                    <div className={`flex items-center ${!(column.minWidth || column.maxWidth) ? 'whitespace-nowrap' : ''}`}>
                                      {column.header || column.headerName}
                                      {column.sortable && sortKey && renderSortIcon(sortKey)}
                                    </div>
                                  </Tooltip>
                                ) : (
                                  <div className={`flex items-center ${!(column.minWidth || column.maxWidth) ? 'whitespace-nowrap' : ''}`}>
                                    {column.header || column.headerName}
                                    {column.sortable && sortKey && renderSortIcon(sortKey)}
                                  </div>
                                )}
                            </th>;
                        })}
                    </tr>
                    <tr
                      data-testid={`${dataTestId}-header-row-subcolumns`}
                      style={{ backgroundColor: 'rgb(var(--color-surface-alt))' }}
                    >
                        {columns.map((column, colIndex) =>
                            column.columns ? column.columns.map((subColumn, subIndex) => <HeaderCell key={`${colIndex}-${subIndex}`} col={subColumn} truncateCells={truncateCells} />) : null
                        )}
                    </tr>
                    {/* 3rd header row — sub-headers (External ID, Status, Landline, etc.) */}
                    {(() => {
                      const allFlatCols = columns.flatMap(c => c.columns || [c]) as ConcreteColumn<T>[];
                      if (!allFlatCols.some(c => c.subHeaderName)) return null;

                      // Build cells for Row 3: only for columns that DON'T have rowSpan=3.
                      // Non-grouped columns without subHeaderName have rowSpan=3 → skip them.
                      // Non-grouped columns with subHeaderName have rowSpan=2 → render sub-header.
                      // Sub-columns of grouped columns → render empty or sub-header.
                      const row3Cells: React.ReactNode[] = [];
                      columns.forEach((column, colIdx) => {
                        if (column.columns) {
                          // Grouped column: render a cell for each sub-column
                          column.columns.forEach((subCol, subIdx) => {
                            const concrete = subCol as ConcreteColumn<T>;
                            if (concrete.subHeaderName) {
                              const subSortKey = concrete.subSortField || undefined;
                              row3Cells.push(
                                <th
                                  key={`${colIdx}-${subIdx}`}
                                  scope="col"
                                  tabIndex={0}
                                  className={`px-3 h-8 text-left text-xs font-medium text-text-muted border-b border-sidebarHeaderBorder relative whitespace-nowrap ${subSortKey ? 'cursor-pointer' : ''}`}
                                  style={{ backgroundColor: 'rgb(var(--color-surface-alt))' }}
                                  onClick={() => subSortKey && handleRequestSort(subSortKey)}
                                  onKeyDown={(e) => {
                                    if (subSortKey && (e.key === 'Enter' || e.key === ' ')) {
                                      e.preventDefault();
                                      handleRequestSort(subSortKey);
                                    }
                                  }}
                                >
                                  <div className="flex items-center">
                                    {concrete.subHeaderName}
                                    {subSortKey && renderSortIcon(subSortKey)}
                                  </div>
                                </th>
                              );
                            } else {
                              row3Cells.push(
                                <td key={`${colIdx}-${subIdx}`} className="eco-th-empty border-b border-sidebarHeaderBorder" aria-hidden="true" style={{ backgroundColor: 'rgb(var(--color-surface-alt))' }}></td>
                              );
                            }
                          });
                        } else {
                          // Non-grouped column
                          const concrete = column as ConcreteColumn<T>;
                          if (concrete.subHeaderName) {
                            // Has subHeaderName → rowSpan=2, need a cell in Row 3
                            const subSortKey = concrete.subSortField || undefined;
                            row3Cells.push(
                              <th
                                key={colIdx}
                                scope="col"
                                tabIndex={0}
                                className={`px-3 h-8 text-left text-xs font-medium text-text-muted border-b border-sidebarHeaderBorder relative whitespace-nowrap ${subSortKey ? 'cursor-pointer' : ''}`}
                                style={{ backgroundColor: 'rgb(var(--color-surface-alt))' }}
                                onClick={() => subSortKey && handleRequestSort(subSortKey)}
                                onKeyDown={(e) => {
                                  if (subSortKey && (e.key === 'Enter' || e.key === ' ')) {
                                    e.preventDefault();
                                    handleRequestSort(subSortKey);
                                  }
                                }}
                              >
                                <div className="flex items-center">
                                  {concrete.subHeaderName}
                                  {subSortKey && renderSortIcon(subSortKey)}
                                </div>
                              </th>
                            );
                          }
                          // No subHeaderName → rowSpan=3, SKIP (already spans into Row 3)
                        }
                      });

                      return (
                        <tr
                          data-testid={`${dataTestId}-header-row-sub2`}
                          style={{ backgroundColor: 'rgb(var(--color-surface-alt))' }}
                        >
                          {row3Cells}
                        </tr>
                      );
                    })()}
                </>
            ) : (
                <tr data-testid={`${dataTestId}-header-row`}>
                    {(prefixColumns || []).flatMap(c => c.columns ? c.columns : [c]).map((col, index) => (
                      <th
                        key={`prefix-header-${index}`}
                        scope="col"
                        tabIndex={0}
                        className="px-0.5 py-2 text-left text-sm font-medium border-b border-sidebarHeaderBorder"
                        style={{
                          width: col.sticky ? undefined : '20px',
                          minWidth: col.field ? columnWidths?.[String(col.field)] : undefined,
                          backgroundColor: 'rgb(var(--color-surface-alt))',
                          ...(col.sticky ? { position: 'sticky', left: col.stickyLeft ?? '0px', zIndex: 32 } : {}),
                        }}
                      >
                        {col.headerName}
                      </th>
                    ))}
                    {selectable && (hideCheckboxes
                      ? <th className="w-12 px-4 py-2 text-left sticky z-20" style={{ minWidth: '48px', width: '48px', backgroundColor: 'rgb(var(--color-surface-alt))', left: (() => { const stickyPfx = (prefixColumns || []).flatMap(c => c.columns ? c.columns : [c]).filter(c => c.sticky); if (stickyPfx.length === 0) return '0px'; return `${stickyPfx.reduce((acc, c) => acc + (parseInt(columnWidths?.[String(c.field)] ?? '40') || 40), 0)}px`; })() }} aria-hidden="true" />
                      : <th scope="col" className="w-12 px-4 py-2 text-left sticky z-20" style={{ backgroundColor: 'rgb(var(--color-surface-alt))', left: (() => { const stickyPfx = (prefixColumns || []).flatMap(c => c.columns ? c.columns : [c]).filter(c => c.sticky); if (stickyPfx.length === 0) return '0px'; return `${stickyPfx.reduce((acc, c) => acc + (parseInt(columnWidths?.[String(c.field)] ?? '40') || 40), 0)}px`; })() }}><IndeterminateCheckbox className="h-4 w-4 rounded border-border text-primary focus:ring-primary" data-testid={`${dataTestId}-select-all-checkbox`} onChange={handleSelectAllClick} onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') { e.preventDefault(); const syntheticEvent = { target: { checked: !allPageRowsSelected } } as React.ChangeEvent<HTMLInputElement>; handleSelectAllClick(syntheticEvent); } }} checked={allPageRowsSelected} indeterminate={somePageRowsSelected} aria-label={`${tableLabel ? t('common.table.selectAllIn', 'Select all rows in {{label}}', { label: tableLabel }) : t('common.table.selectAll', 'Select all rows')}, ${somePageRowsSelected ? t('common.partiallyChecked', 'partially checked') : allPageRowsSelected ? t('common.checked', 'checked') : t('common.notChecked', 'not checked')}`} /></th>
                    )}
                    {renderSubComponent && (
                      <th scope="col" tabIndex={0} className="w-12 px-3 border-b border-sidebarHeaderBorder text-center">
                        {renderExpandColumnHeader ?? null}
                      </th>
                    )}
                    {columns.map((column, index) => <HeaderCell key={index} col={column as ConcreteColumn<T>} truncateCells={truncateCells} />)}
                </tr>
            )}
        </thead>
        <tbody
          className="bg-surface"
          data-testid={`${dataTestId}-tbody`}
        >
            {!isLoading && !error && renderTopBanner && (
              <tr data-testid={`${dataTestId}-top-banner`}>
                <td
                  colSpan={
                    columns.flatMap(c => c.columns || [c]).length +
                    (prefixColumns ? prefixColumns.flatMap(c => c.columns || [c]).length : 0) +
                    (selectable ? 1 : 0) +
                    (renderSubComponent ? 1 : 0)
                  }
                  className="p-0"
                >
                  {renderTopBanner}
                </td>
              </tr>
            )}
            {isLoading ? null : error ? (
                <tr>
                    <td
                        colSpan={columns.flatMap(c => c.columns || [c]).length + (prefixColumns ? prefixColumns.flatMap(c => c.columns || [c]).length : 0) + (selectable ? 1 : 0) + (renderSubComponent ? 1 : 0)}
                        className="text-center py-8 text-error"
                    >
                        {typeof error === "string" ? t(error, { defaultValue: t('common.error') }) : t('common.error')}
                    </td>
                </tr>
            ) : currentRows.length === 0 ? (
                <tr>
                    <td
                        colSpan={columns.flatMap(c => c.columns || [c]).length + (prefixColumns ? prefixColumns.flatMap(c => c.columns || [c]).length : 0) + (selectable ? 1 : 0) + (renderSubComponent ? 1 : 0)}
                        className={naturalHeight ? 'py-16 text-center' : 'p-0 h-px'}
                    >
                        {naturalHeight && <NoResultsFound />}
                    </td>
                </tr>
            ) : (
                currentRows.map((row, rowIndex) => (
                    <DataTableRow
                        key={`${String(row.id)}-${rowIndex}`}
                        row={row}
                        rowRenderKey={`${String(row.id)}-${rowIndex}`}
                        rowNumber={page * rowsPerPage + rowIndex + 1}
                        selected={selected}
                        columns={columns}
                        prefixColumns={prefixColumns ?? []}
                        renderSubComponent={renderSubComponent}
                        renderInlineSubComponent={renderInlineSubComponent}
                        onSelect={handleSelect}
                        selectable={selectable}
                        hideCheckboxes={hideCheckboxes}
                        columnWidths={columnWidths}
                        onRowClick={onRowClick}
                        isRowSelectable={isRowSelectable}
                        renderUnselectableCell={renderUnselectableCell}
                        truncateCells={truncateCells}
                        rowClassName={getRowClassName?.(row) ?? ''}
                        rowHeight={rowHeight}
                        defaultOpen={expandAllRows ?? false}
                        {...(onRowExpandChange ? { onRowExpandChange } : {})}
                    />
                ))
            )}
          </tbody>
          </table>
        </div>
        {!hidePagination && totalCount > 0 && (
          <div className={`border-t border-sidebarHeaderBorder bg-surface z-10 rounded-b-[8px] flex-shrink-0${naturalHeight ? '' : ' sticky bottom-0'}`}>
            <Pagination
              data-testid={`${dataTestId}-pagination`}
              currentPage={page + 1}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              totalRecords={totalCount}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={rowsPerPageOptions || [10, 50, 100, 500, 1000]}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DataTable;
